import { useEffect, useRef, useState } from 'react'
import {
  resolveAlertConsumers,
  type APIConsumerRef,
} from '@/services/notificationService'

/** The chip text of a consumer: water card, name and address. */
export const consumerLabel = (ref: APIConsumerRef) =>
  [ref.waterCard, ref.name, ref.address]
    .filter(part => part != null && part !== '')
    .join(' · ')

/**
 * Names the stored service ids of a consumers condition. A tree stores ids only, so
 * the ids not yet known are resolved in one call; `remember` takes consumers the
 * author just picked, so those chips never wait for the Gateway. An id the Gateway
 * does not return (deleted, other site) is asked once and then shows as the bare id.
 */
export function useConsumerChips(siteId: string | undefined, values: string[]) {
  const [refs, setRefs] = useState<Record<string, APIConsumerRef>>({})
  const asked = useRef(new Set<string>())

  const remember = (picked: APIConsumerRef) => {
    asked.current.add(String(picked.serviceId))
    setRefs(prev => ({ ...prev, [String(picked.serviceId)]: picked }))
  }

  useEffect(() => {
    if (!siteId) return
    const unknown = values.filter(v => !refs[v] && !asked.current.has(v))
    const ids = unknown.map(Number).filter(Number.isSafeInteger)
    unknown.forEach(v => asked.current.add(v))
    if (ids.length === 0) return
    resolveAlertConsumers(siteId, ids)
      .then(found =>
        setRefs(prev => ({
          ...prev,
          ...Object.fromEntries(found.map(r => [String(r.serviceId), r])),
        }))
      )
      .catch(err => {
        // Let a later render ask again; the chips show the bare ids meanwhile.
        unknown.forEach(v => asked.current.delete(v))
        console.error('[consumers] Failed to name the stored consumers:', err)
      })
  }, [siteId, values, refs])

  const labelOf = (value: string) =>
    refs[value] ? consumerLabel(refs[value]) : value

  return { labelOf, remember }
}
